import React, { useState, useEffect } from 'react';
import { useTextExpansion } from '../contexts/TextExpansionContext';
import { getTranslations } from '../utils/translations';

// Bible book names in English and Spanish for scripture reference detection
const BIBLE_BOOKS = [
  // English
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
  'Joshua', 'Judges', 'Ruth', 'Samuel', 'Kings', 'Chronicles',
  'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms?', 'Proverbs',
  'Ecclesiastes', 'Song of Solomon', 'Isaiah', 'Jeremiah',
  'Lamentations', 'Ezekiel', 'Daniel', 'Hosea', 'Joel', 'Amos',
  'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk', 'Zephaniah',
  'Haggai', 'Zechariah', 'Malachi', 'Matthew', 'Mark', 'Luke',
  'John', 'Acts', 'Romans', 'Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', 'Thessalonians', 'Timothy', 'Titus',
  'Philemon', 'Hebrews', 'James', 'Peter', 'Jude', 'Revelation',
  // Spanish
  'Génesis', 'Éxodo', 'Levítico', 'Números', 'Deuteronomio',
  'Josué', 'Jueces', 'Rut', 'Crónicas',
  'Esdras', 'Nehemías', 'Ester', 'Salmos?',
  'Proverbios', 'Eclesiastés', 'Cantares', 'Isaías', 'Jeremías',
  'Lamentaciones', 'Oseas', 'Amós',
  'Abdías', 'Jonás', 'Miqueas', 'Nahúm', 'Habacuc', 'Sofonías',
  'Hageo', 'Zacarías', 'Malaquías', 'Mateo', 'Marcos', 'Lucas',
  'Juan', 'Hechos', 'Romanos', 'Corintios', 'Gálatas', 'Efesios',
  'Filipenses', 'Colosenses', 'Tesalonicenses', 'Timoteo',
  'Filemón', 'Hebreos', 'Santiago', 'Pedro', 'Judas', 'Apocalipsis'
];

const SCRIPTURE_PHRASES = ['Pasajes bíblicos selectos', 'Selected Scripture Passages'];

/**
 * Formats text by placing leading scripture references on their own line.
 * E.g. "1 Samuel 13–15 The Bible includes..." becomes two lines:
 *   "1 Samuel 13–15"
 *   "The Bible includes..."
 */
const formatScriptureText = (text) => {
  if (!text) return [text];

  // Check for special phrases at the start
  for (const phrase of SCRIPTURE_PHRASES) {
    if (text.startsWith(phrase)) {
      const rest = text.slice(phrase.length).trim();
      return rest ? [phrase, rest] : [text];
    }
  }

  // Build regex for scripture reference at start of text
  // Matches: optional "1 "/"2 "/"3 " + book name + chapter/verse like "13–15" or "22:1–14" or "3:1–18"
  const booksPattern = BIBLE_BOOKS.join('|');
  const scriptureRegex = new RegExp(
    `^([123]\\s)?(?:${booksPattern})\\s[\\d]+(?:[:][\\d]+)?(?:[–\\-;,\\s]+[\\d]+(?:[:][\\d]+)?)*`
  );

  const match = text.match(scriptureRegex);
  if (match) {
    const reference = match[0].trim();
    const rest = text.slice(match[0].length).trim();
    if (rest) {
      return [reference, rest];
    }
  }

  return [text];
};

const ReadMoreText = ({ text, seriesPage, maxLength, messageSpeaker, textColor = '#fff', scriptureReference = '' }) => {
  const t = getTranslations();
  console.log({seriesPage});
  
  const [isExpanded, setIsExpanded] = useState(false);
  const { handleTextExpansion, stopVideoPlayer, isTextExpanded } = useTextExpansion();
  
  // Sync local state with context state
  useEffect(() => {
    console.log('🔄 ReadMoreText: Context isTextExpanded changed to:', isTextExpanded);
    setIsExpanded(isTextExpanded);
  }, [isTextExpanded]);
  

  const toggleExpansion = () => {
    console.log('❗❗❗ TOGGLE EXPANSION CALLED!!!');
    const newExpanded = !isExpanded;
    console.log('🔄 ReadMoreText toggleExpansion:', { from: isExpanded, to: newExpanded });
    setIsExpanded(newExpanded);
    
    // Use context to handle text expansion
    console.log('📤 Calling handleTextExpansion with:', newExpanded);
    handleTextExpansion(newExpanded);
  };

  // Notify context on mount if text is long enough to be expandable
  useEffect(() => {
    const shouldTruncate = text && text.length > maxLength;
    if (handleTextExpansion && shouldTruncate) {
      handleTextExpansion(false); // Initially not expanded
    }
  }, [text, maxLength]); // Removed handleTextExpansion from dependencies

  const shouldTruncate = text && text.length > maxLength;
  // Prefer the authoritative ifl:scripture_reference field (populated by editors).
  // Fall back to regex-parsing the description's leading text for items not yet backfilled.
  const scriptureParts = scriptureReference
    ? (() => {
        const regexParts = formatScriptureText(text);
        return regexParts.length > 1
          ? [scriptureReference, regexParts[1]]
          : [scriptureReference, text || ''];
      })()
    : formatScriptureText(text);
  const hasScriptureRef = scriptureParts.length > 1;

  const renderFormattedText = () => scriptureParts.map((part, i) => (
    <div key={i} style={i === 0 && hasScriptureRef ? { marginBottom: '4px' } : {}}>{part}</div>
  ));

  const fullSeriesButton = <a 
        id="cphPlayer_C001_LibraryDetail_Resources_lnkMessageMate" 
        className="btn" 
        data-toggle="tooltip" 
        data-placement="top" 
        title={messageSpeaker === 'Paws & Tales' ? t.fullSeason : t.fullSeries} 
        data-original-title={messageSpeaker === 'Paws & Tales' ? t.fullSeason : t.fullSeries} 
        href={seriesPage} 
        target="_blank" 
        rel="noreferrer"
        onClick={(e) => {
          console.log(`🛑 Full ${messageSpeaker === 'Paws & Tales' ? 'Season' : 'Series'} button clicked - stopping video player`);
          stopVideoPlayer();
          // Link will still open in new tab due to target="_blank"
        }}
        style={{ margin: '5px 0px', width: '131.21px' }}
      >
        {messageSpeaker === 'Paws & Tales' ? t.fullSeason : t.fullSeries}
      </a>;

  return (
    <div id="read-more-text" style={{ color: textColor }}>
      {shouldTruncate ? (
        <div style={{ flexDirection: 'column' }}>
          {isExpanded ? renderFormattedText() : (
            hasScriptureRef ? (
              <>
                <div style={{ marginBottom: '4px' }}>{scriptureParts[0]}</div>
                <div>{`${scriptureParts[1].substring(0, maxLength)}...`}</div>
              </>
            ) : `${text.substring(0, maxLength)}...`
          )}
          <div>      
            <a 
              role="button"
              tabIndex="0"
              onClick={(e) => {
                e.preventDefault();
                toggleExpansion();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpansion();
                }
              }}
              style={{ 
                color: textColor, 
                textDecoration: 'none', 
                cursor: 'pointer',
                fontSize: 'inherit',
                fontWeight: 'normal'
              }}
            >
              {isExpanded ? `- ${t.showLess}` : `+ ${t.readMore}`}
            </a>
          </div>
          {isExpanded && seriesPage && <div style={{ marginTop: '10px' }}>{fullSeriesButton}</div>}
        </div>
      ) : (
        <>
          {renderFormattedText()}
          {isExpanded && seriesPage && fullSeriesButton}
        </>
      )}
    </div>
  );
};

export default ReadMoreText;